<template>
  <v-container fluid class="dashboard-container">
    <!-- Header Section -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="dashboard-header">
          <h1 class="dashboard-title">
            <v-icon class="title-icon" size="large">mdi-account-group</v-icon>
            สถิติบุคลากรการศึกษา
          </h1>
          <p class="dashboard-subtitle">
            ข้อมูลจำนวนพนักงานราชการและครูอัตราจ้าง
          </p>
        </div>
      </v-col>
    </v-row>

    <!-- Filter Section -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card class="filter-card" elevation="8">
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="searchText"
                  label="ค้นหาสาขาวิชา"
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  dense
                  clearable
                  hide-details
                  class="search-field"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="selectedSubjectType"
                  :items="subjectTypeOptions"
                  label="ประเภทวิชา"
                  outlined
                  dense
                  clearable
                  hide-details
                  class="filter-select"
                ></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="selectedSortBy"
                  :items="sortOptions"
                  label="เรียงลำดับ"
                  outlined
                  dense
                  hide-details
                  class="filter-select"
                ></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="selectedChartType"
                  :items="chartTypeOptions"
                  label="ประเภทแผนภูมิ"
                  outlined
                  dense
                  hide-details
                  class="filter-select"
                ></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-btn
                  color="primary"
                  @click="resetFilters"
                  outlined
                  block
                  class="reset-btn"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  รีเซ็ต
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Stats Cards -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="stats-card stats-card-1" elevation="8">
          <v-card-text class="text-center">
            <v-icon class="stats-icon" size="48">mdi-account-tie</v-icon>
            <div class="stats-number">{{ filteredTotalGov }}</div>
            <div class="stats-label">พนักงานราชการ</div>
            <div class="stats-sublabel">{{ filteredTotalGov > 0 ? ((filteredTotalGov / filteredTotalAll) * 100).toFixed(1) : 0 }}%</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stats-card stats-card-2" elevation="8">
          <v-card-text class="text-center">
            <v-icon class="stats-icon" size="48">mdi-school</v-icon>
            <div class="stats-number">{{ filteredTotalTem }}</div>
            <div class="stats-label">ครูอัตราจ้าง</div>
            <div class="stats-sublabel">{{ filteredTotalTem > 0 ? ((filteredTotalTem / filteredTotalAll) * 100).toFixed(1) : 0 }}%</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stats-card stats-card-3" elevation="8">
          <v-card-text class="text-center">
            <v-icon class="stats-icon" size="48">mdi-chart-pie</v-icon>
            <div class="stats-number">{{ filteredTotalSubjects }}</div>
            <div class="stats-label">สาขาวิชา</div>
            <div class="stats-sublabel">{{ filteredData.length > 0 ? 'ที่แสดง' : 'ไม่พบข้อมูล' }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stats-card stats-card-4" elevation="8">
          <v-card-text class="text-center">
            <v-icon class="stats-icon" size="48">mdi-account-multiple</v-icon>
            <div class="stats-number">{{ filteredTotalAll }}</div>
            <div class="stats-label">รวมทั้งหมด</div>
            <div class="stats-sublabel">{{ originalData.length }} สาขาทั้งหมด</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Section -->
    <v-row>
      <v-col cols="12" lg="6" class="mb-4">
        <v-card class="chart-card" elevation="12">
          <v-card-title class="chart-title">
            <v-icon class="mr-3" color="primary">mdi-account-tie</v-icon>
            พนักงานราชการ สายการสอน
            <v-spacer></v-spacer>
            <v-btn icon size="small" @click="refreshData" class="ml-2">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div class="chart-container" :class="{ loading: loading }">             
              <apexchart
                v-if="filteredSeriesGov.length > 0"
                :type="selectedChartType"
                height="450"
                :options="chartOptionsGov"
                :series="filteredSeriesGov"
              ></apexchart>
              <div v-else class="no-data-message">
                <v-icon size="48" color="grey">mdi-chart-pie</v-icon>
                <p>ไม่พบข้อมูลตามเงื่อนไขที่กำหนด</p>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="6" class="mb-4">
        <v-card class="chart-card" elevation="12">
          <v-card-title class="chart-title">
            <v-icon class="mr-3" color="secondary">mdi-school</v-icon>
            ครูอัตราจ้าง
            <v-spacer></v-spacer>
            <v-btn icon size="small" @click="refreshData" class="ml-2">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div class="chart-container" :class="{ loading: loading }">
             
              <apexchart
                v-if="filteredSeriesTem.length > 0"
                :type="selectedChartType"
                height="450"
                :options="chartOptionsTem"
                :series="filteredSeriesTem"
              ></apexchart>
              <div v-else class="no-data-message">
                <v-icon size="48" color="grey">mdi-chart-pie</v-icon>
                <p>ไม่พบข้อมูลตามเงื่อนไขที่กำหนด</p>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Detailed Table -->
    <v-row>
      <v-col cols="12">
        <v-card class="table-card" elevation="8">
          <v-card-title class="table-title">
            <v-icon class="mr-3">mdi-table</v-icon>
            รายละเอียดข้อมูล
            <v-spacer></v-spacer>
            <v-btn
              color="success"
              @click="exportToExcel"
              outlined
              small
              class="export-btn"
            >
              <v-icon left small>mdi-file-excel</v-icon>
              Export Excel
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="tableHeaders"
              :items="filteredTableData"
              :items-per-page="itemsPerPage"
              :footer-props="{
                'items-per-page-options': [10, 25, 50, 100, -1],
                'items-per-page-text': 'รายการต่อหน้า:'
              }"
              class="modern-table"
              :loading="loading"
              loading-text="กำลังโหลดข้อมูล..."
              sort-by="total_raw"
              sort-desc
            >
              <template v-slot:item.gov_count="{ item }">
                <v-chip
                  :color="getChipColor(item.gov_count_raw, 'gov')"
                  text-color="white"
                  small
                >
                  {{ item.gov_count }}
                </v-chip>
              </template>
              <template v-slot:item.tem_count="{ item }">
                <v-chip
                  :color="getChipColor(item.tem_count_raw, 'tem')"
                  text-color="white"
                  small
                >
                  {{ item.tem_count }}
                </v-chip>
              </template>
              <template v-slot:item.percentage_gov="{ item }">
                <div class="percentage-cell">
                  <v-progress-linear
                    :value="parseFloat(item.percentage_gov)"
                    height="6"
                    color="primary"
                    background-color="grey lighten-3"
                    class="percentage-bar"
                  ></v-progress-linear>
                  <span class="percentage-text">{{ item.percentage_gov }}%</span>
                </div>
              </template>
              <template v-slot:item.percentage_tem="{ item }">
                <div class="percentage-cell">
                  <v-progress-linear
                    :value="parseFloat(item.percentage_tem)"
                    height="6"
                    color="secondary"
                    background-color="grey lighten-3"
                    class="percentage-bar"
                  ></v-progress-linear>
                  <span class="percentage-text">{{ item.percentage_tem }}%</span>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Summary Section -->
    <v-row class="mt-6">
      <v-col cols="12">
        <v-card class="summary-card" elevation="8">
          <v-card-title class="summary-title">
            <v-icon class="mr-3">mdi-chart-line</v-icon>
            สรุปภาพรวม
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <div class="summary-item">
                  <div class="summary-label">อัตราส่วนเฉลี่ย พนักงานราชการ</div>
                  <div class="summary-value primary--text">{{ avgGovPercentage }}%</div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="summary-item">
                  <div class="summary-label">อัตราส่วนเฉลี่ย ครูอัตราจ้าง</div>
                  <div class="summary-value secondary--text">{{ avgTemPercentage }}%</div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="summary-item">
                  <div class="summary-label">สาขาที่มีบุคลากรมากที่สุด</div>
                  <div class="summary-value success--text">{{ topSubject }}</div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import VueMoment from "vue-moment";
import moment from "moment-timezone";

export default {
  name: "personnel_etc",
  data() {
    return {
      VueMoment,
      moment,
      loading: false,
      ApiKey: "HRvec2021",
      valid: true,
      
      // Original data
      originalData: [],
      
      // Filter states
      searchText: "",
      selectedSubjectType: null,
      selectedSortBy: "total_desc",
      selectedChartType: "pie",
      
      // Table pagination
      itemsPerPage: 25,
      
      // Filter options
      subjectTypeOptions: [],
      sortOptions: [
        { text: "รวมทั้งหมด (มาก-น้อย)", value: "total_desc" },
        { text: "รวมทั้งหมด (น้อย-มาก)", value: "total_asc" },
        { text: "พนักงานราชการ (มาก-น้อย)", value: "gov_desc" },
        { text: "พนักงานราชการ (น้อย-มาก)", value: "gov_asc" },
        { text: "ครูอัตราจ้าง (มาก-น้อย)", value: "tem_desc" },
        { text: "ครูอัตราจ้าง (น้อย-มาก)", value: "tem_asc" },
        { text: "ชื่อสาขา (ก-ฮ)", value: "name_asc" },
        { text: "ชื่อสาขา (ฮ-ก)", value: "name_desc" }
      ],
      chartTypeOptions: [
        { text: "แผนภูมิวงกลม", value: "pie" },
        { text: "แผนภูมิโดนัท", value: "donut" },
        { text: "แผนภูมิแท่ง", value: "bar" }
      ],

      // Chart base options
      baseChartOptions: {
        chart: {
          animations: {
            enabled: true,
            easing: "easeout",
            speed: 1000,
            animateGradually: {
              enabled: true,
              delay: 150
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350
            }
          },
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          }
        },
        colors: [
          "#6366F1", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981", 
          "#EF4444", "#06B6D4", "#84CC16", "#F97316", "#8B5A2B"
        ],
        plotOptions: {
          pie: {
            donut: {
              size: "45%"
            },
            dataLabels: {
              offset: -10
            }
          },
          bar: {
            horizontal: false,
            columnWidth: "55%",
            endingShape: "rounded"
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function(val, opts) {
            const name = opts.w.globals.labels[opts.seriesIndex];
            const cleanName = name ? name.split(" (")[0] : "";
            return [cleanName, val.toFixed(1) + "%"];
          },
          style: {
            fontSize: "11px",
            fontWeight: "bold"
          }
        },
        legend: {
          position: "bottom",
          fontSize: "14px",
          fontWeight: 500,
          markers: {
            width: 12,
            height: 12,
            radius: 6
          }
        },
        tooltip: {
          theme: "dark",
          y: {
            formatter: function(val, opts) {
              if (opts && opts.w && opts.w.globals.labels) {
                const label = opts.w.globals.labels[opts.dataPointIndex];
                const count = label ? label.match(/\((\d+) คน\)/) : null;
                return count ? count[1] + " คน" : val + " คน";
              }
              return val + " คน";
            }
          }
        },
        responsive: [
          {
            breakpoint: 768,
            options: {
              legend: {
                position: "bottom"
              }
            }
          }
        ]
      },

      tableHeaders: [
        {
          text: "ประเภทวิชา",
          value: "subject_typeName",
          align: "start",
          width: "200px",
          sortable: true
        },
        {
          text: "กลุ่มวิชา",
          value: "branch_name_th",
          align: "start",
          width: "180px",
          sortable: true
        },
        {
          text: "พนักงานราชการ",
          value: "gov_count",
          align: "center",
          width: "140px",
          sortable: true
        },
        {
          text: "% ราชการ",
          value: "percentage_gov",
          align: "center",
          width: "150px",
          sortable: true
        },
        {
          text: "ครูอัตราจ้าง",
          value: "tem_count",
          align: "center",
          width: "130px",
          sortable: true
        },
        {
          text: "% อัตราจ้าง",
          value: "percentage_tem",
          align: "center",
          width: "150px",
          sortable: true
        },
        { 
          text: "รวม (คำนวณ)", 
          value: "total", 
          align: "center", 
          width: "130px",
          sortable: true
        },
        {
          text: "รวม (ข้อมูล)",
          value: "total_all",
          align: "center",
          width: "130px",
          sortable: true
        }
      ],

      // Debounce timer
      debounceSearch: null
    };
  },

  computed: {
    // Filtered data based on search and filters
    filteredData() {
      let filtered = [...this.originalData];

      // Search filter
      if (this.searchText) {
        const searchLower = this.searchText.toLowerCase();
        filtered = filtered.filter(item => 
          item.subject_typeName.toLowerCase().includes(searchLower) ||
          item.branch_name_th.toLowerCase().includes(searchLower)
        );
      }

      // Subject type filter
      if (this.selectedSubjectType) {
        filtered = filtered.filter(item => 
          item.subject_typeName === this.selectedSubjectType
        );
      }

      // Sort
      const [sortField, sortOrder] = this.selectedSortBy.split("_");
      filtered.sort((a, b) => {
        let aVal, bVal;
        
        switch (sortField) {
          case "total":
            aVal = a.total_raw;
            bVal = b.total_raw;
            break;
          case "gov":
            aVal = a.gov_count_raw;
            bVal = b.gov_count_raw;
            break;
          case "tem":
            aVal = a.tem_count_raw;
            bVal = b.tem_count_raw;
            break;
          case "name":
            aVal = a.subject_typeName;
            bVal = b.subject_typeName;
            break;
          default:
            aVal = a.total_raw;
            bVal = b.total_raw;
        }

        if (sortOrder === "asc") {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      return filtered;
    },

    // Chart data based on filtered data
    filteredSeriesGov() {
      return this.filteredData.map(item => item.gov_count_raw);
    },

    filteredSeriesTem() {
      return this.filteredData.map(item => item.tem_count_raw);
    },

    filteredLabelsGov() {
      return this.filteredData.map(item => 
        `${item.subject_typeName} (${item.gov_count_raw} คน)`
      );
    },

    filteredLabelsTem() {
      return this.filteredData.map(item => 
        `${item.subject_typeName} (${item.tem_count_raw} คน)`
      );
    },

    // Dynamic chart options
    chartOptionsGov() {
      return {
        ...this.baseChartOptions,
        labels: this.filteredLabelsGov,
        chart: {
          ...this.baseChartOptions.chart,
          type: this.selectedChartType
        },
        title: {
          text: "พนักงานราชการ สายการสอน",
          align: "center",
          style: {
            fontSize: "18px",
            fontWeight: 600,
            color: "#374151"
          }
        }
      };
    },

    chartOptionsTem() {
      return {
        ...this.baseChartOptions,
        labels: this.filteredLabelsTem,
        chart: {
          ...this.baseChartOptions.chart,
          type: this.selectedChartType
        },
        title: {
          text: "ครูอัตราจ้าง",
          align: "center",
          style: {
            fontSize: "18px",
            fontWeight: 600,
            color: "#374151"
          }
        }
      };
    },

    // Table data
    filteredTableData() {
      return this.filteredData.map(item => ({
        ...item,
        gov_count: item.gov_count_raw.toLocaleString(),
        tem_count: item.tem_count_raw.toLocaleString(),
        total: item.total_raw.toLocaleString(),
        total_all: item.total_all_raw.toLocaleString()
      }));
    },

    // Summary statistics
    filteredTotalGov() {
      return this.filteredData.reduce((sum, item) => sum + item.gov_count_raw, 0);
    },

    filteredTotalTem() {
      return this.filteredData.reduce((sum, item) => sum + item.tem_count_raw, 0);
    },

    filteredTotalAll() {
      return this.filteredTotalGov + this.filteredTotalTem;
    },

    filteredTotalSubjects() {
      return this.filteredData.length;
    },

    avgGovPercentage() {
      if (this.filteredData.length === 0) return 0;
      const total = this.filteredData.reduce((sum, item) => 
        sum + parseFloat(item.percentage_gov), 0
      );
      return (total / this.filteredData.length).toFixed(1);
    },

    avgTemPercentage() {
      if (this.filteredData.length === 0) return 0;
      const total = this.filteredData.reduce((sum, item) => 
        sum + parseFloat(item.percentage_tem), 0
      );
      return (total / this.filteredData.length).toFixed(1);
    },

    topSubject() {
      if (this.filteredData.length === 0) return "ไม่พบข้อมูล";
      const maxItem = this.filteredData.reduce((max, item) => 
        item.total_raw > max.total_raw ? item : max
      );
      return maxItem.subject_typeName;
    }
  },

  watch: {
    // Watch for filter changes to update charts
    filteredData() {
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    searchText() {
      this.setupSearchDebounce();
    }
  },

  created() {
    this.personnel_etcQueryAll();
  },

  methods: {
    async personnel_etcQueryAll() {
      this.loading = true;
      try {
        let result = await this.$http.post("personnel_etc.php", {
          ApiKey: this.ApiKey,
          subjectCount: "Ok"
        });     
        if (result.data && result.data.length > 0) {
          console.log("First item keys:", Object.keys(result.data[0]));
          console.log("First item values:", result.data[0]);
        }

        // Clear previous data
        this.originalData = [];
        const subjectTypes = new Set();

        result.data.forEach((element, index) => {
          if (index < 3) {
            console.log(`Element ${index}:`, element);
          }

          const govCount = parseInt(element.countPersonSubjectTeachGov) || 0;
          const temCount = parseInt(element.countPersonSubjectTeachTem) || 0;
          const totalCount = parseInt(element.countPersonSubjectTeach) || 0;
          const actualTotal = govCount + temCount;

          const subjectName = element.subject_typeName || 
                              element.subjectTypeName || 
                              element.subject_type_name || 
                              element.type_name || 
                              "ไม่ระบุ";

          const branchName = element.branch_name_th || 
                            element.branchNameTh || 
                            element.branch_name || 
                            element.branch || 
                            "";

          // Add to subject type options
          subjectTypes.add(subjectName);

          // Create data object
          const dataItem = {
            subject_typeName: subjectName,
            branch_name_th: branchName,
            gov_count_raw: govCount,
            tem_count_raw: temCount,
            total_raw: actualTotal,
            total_all_raw: totalCount,
            percentage_gov: actualTotal > 0 ? ((govCount / actualTotal) * 100).toFixed(1) : "0.0",
            percentage_tem: actualTotal > 0 ? ((temCount / actualTotal) * 100).toFixed(1) : "0.0"
          };

          this.originalData.push(dataItem);
        });

        // Update subject type options
        this.subjectTypeOptions = Array.from(subjectTypes).sort().map(type => ({
          text: type,
          value: type
        }));

        console.log("=== FINAL ORIGINAL DATA ===");
        console.log("Original data length:", this.originalData.length);
        console.log("Original data sample:", this.originalData.slice(0, 2));

      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        this.loading = false;
      }
    },

    refreshData() {
      this.personnel_etcQueryAll();
    },

    resetFilters() {
      this.searchText = "";
      this.selectedSubjectType = null;
      this.selectedSortBy = "total_desc";
      this.selectedChartType = "pie";
    },

    getChipColor(value, type) {
      if (type === 'gov') {
        if (value >= 50) return 'success';
        if (value >= 20) return 'warning';
        return 'error';
      } else {
        if (value >= 30) return 'success';
        if (value >= 10) return 'warning';
        return 'error';
      }
    },

    exportToExcel() {
      try {
        // Prepare data for export
        const exportData = this.filteredTableData.map(item => ({
          'ประเภทวิชา': item.subject_typeName,
          'กลุ่มวิชา': item.branch_name_th,
          'พนักงานราชการ': item.gov_count_raw,
          'เปอร์เซ็นต์ราชการ': item.percentage_gov,
          'ครูอัตราจ้าง': item.tem_count_raw,
          'เปอร์เซ็นต์อัตราจ้าง': item.percentage_tem,
          'รวมคำนวณ': item.total_raw,
          'รวมข้อมูล': item.total_all_raw
        }));

        // Create CSV content
        const headers = Object.keys(exportData[0]);
        const csvContent = [
          headers.join(','),
          ...exportData.map(row => 
            headers.map(header => `"${row[header]}"`).join(',')
          )
        ].join('\n');

        // Create and download file
        const blob = new Blob(['\ufeff' + csvContent], { 
          type: 'text/csv;charset=utf-8;' 
        });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `personnel_data_${this.moment().format('YYYY-MM-DD')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Show success message (if using toast)
        if (this.$toast) {
          this.$toast.success('ส่งออกข้อมูลเรียบร้อย');
        }
      } catch (error) {
        console.error('Export error:', error);
        if (this.$toast) {
          this.$toast.error('เกิดข้อผิดพลาดในการส่งออกข้อมูล');
        }
      }
    },

    // Performance optimization
    setupSearchDebounce() {
      if (this.debounceSearch) {
        clearTimeout(this.debounceSearch);
      }
      
      this.debounceSearch = setTimeout(() => {
        // Trigger search after 300ms of no typing
        this.$forceUpdate();
      }, 300);
    }
  },

  mounted() {
    // Any initialization code here
  },

  beforeDestroy() {
    if (this.debounceSearch) {
      clearTimeout(this.debounceSearch);
    }
  }
};
</script>

<style scoped>
.dashboard-container {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  padding: 24px;
}

.dashboard-header {
  text-align: center;
  color: white;
  margin-bottom: 2rem;
}

.dashboard-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.title-icon {
  color: #fde68a !important;
}

.dashboard-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  font-weight: 300;
}

/* Filter Card */
.filter-card {
  border-radius: 16px !important;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  transition: all 0.3s ease;
}

.filter-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1) !important;
}

.search-field, .filter-select {
  transition: all 0.2s ease;
}

.search-field:focus-within, .filter-select:focus-within {
  transform: translateY(-2px);
}

.reset-btn {
  height: 40px;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.reset-btn:hover {
  transform: translateY(-1px);
}

/* Stats Cards */
.stats-card {
  border-radius: 16px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.stats-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  pointer-events: none;
}

.stats-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
}

.stats-card-1 {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.stats-card-2 {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
}

.stats-card-3 {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
}

.stats-card-4 {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  color: white;
}

.stats-icon {
  margin-bottom: 1rem;
  opacity: 0.9;
}

.stats-number {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.stats-label {
  font-size: 1rem;
  opacity: 0.9;
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.stats-sublabel {
  font-size: 0.85rem;
  opacity: 0.8;
  font-weight: 300;
}

/* Chart Cards */
.chart-card {
  border-radius: 20px !important;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  transition: all 0.3s ease;
}

.chart-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1) !important;
}

.chart-title {
  font-size: 1.3rem !important;
  font-weight: 600 !important;
  color: #374151 !important;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1rem !important;
}

.chart-container {
  position: relative;
  border-radius: 12px;
  transition: all 0.3s ease;
  min-height: 450px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-container.loading {
  opacity: 0.7;
}

.chart-loading {
  border-radius: 12px;
}

.no-data-message {
  text-align: center;
  color: #6b7280;
  font-size: 1.1rem;
  padding: 2rem;
}

.no-data-message p {
  margin-top: 1rem;
  font-weight: 500;
}

/* Table Card */
.table-card {
  border-radius: 20px !important;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  transition: all 0.3s ease;
}

.table-title {
  font-size: 1.3rem !important;
  font-weight: 600 !important;
  color: #374151 !important;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1rem !important;
}

.export-btn {
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.export-btn:hover {
  transform: translateY(-1px);
}

.modern-table {
  border-radius: 12px;
  overflow: hidden;
}

.modern-table >>> .v-data-table__wrapper {
  border-radius: 12px;
}

.modern-table >>> th {
  background: #f8fafc !important;
  font-weight: 600 !important;
  color: #374151 !important;
}

.modern-table >>> td {
  border-bottom: 1px solid #e5e7eb !important;
}

.modern-table >>> tbody tr:hover {
  background: #f8fafc !important;
}

.percentage-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.percentage-bar {
  flex: 1;
  border-radius: 3px;
}

.percentage-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  min-width: 45px;
}

/* Summary Card */
.summary-card {
  border-radius: 20px !important;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  transition: all 0.3s ease;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1) !important;
}

.summary-title {
  font-size: 1.3rem !important;
  font-weight: 600 !important;
  color: #374151 !important;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1rem !important;
}

.summary-item {
  text-align: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.summary-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.summary-label {
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.summary-value {
  font-size: 1.5rem;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Responsive Design */
@media (max-width: 768px) {
  .dashboard-title {
    font-size: 2rem;
    flex-direction: column;
    gap: 0.5rem;
  }

  .stats-number {
    font-size: 2rem;
  }

  .chart-card {
    margin-bottom: 1rem;
  }

  .chart-container {
    min-height: 350px;
  }

  .summary-item {
    margin-bottom: 1rem;
  }

  .filter-card .v-row {
    flex-direction: column;
  }

  .filter-card .v-col {
    margin-bottom: 0.5rem;
  }
}

@media (max-width: 480px) {
  .dashboard-container {
    padding: 16px;
  }

  .dashboard-title {
    font-size: 1.75rem;
  }

  .stats-number {
    font-size: 1.75rem;
  }

  .chart-container {
    min-height: 300px;
  }

  .modern-table >>> th,
  .modern-table >>> td {
    font-size: 0.8rem;
    padding: 8px 4px;
  }
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.stats-card,
.chart-card,
.table-card,
.summary-card,
.filter-card {
  animation: fadeInUp 0.6s ease-out;
}

.stats-card:nth-child(1) {
  animation: slideInLeft 0.6s ease-out;
  animation-delay: 0.1s;
}

.stats-card:nth-child(2) {
  animation: slideInLeft 0.6s ease-out;
  animation-delay: 0.2s;
}

.stats-card:nth-child(3) {
  animation: slideInRight 0.6s ease-out;
  animation-delay: 0.3s;
}

.stats-card:nth-child(4) {
  animation: slideInRight 0.6s ease-out;
  animation-delay: 0.4s;
}

.chart-card:nth-child(1) {
  animation-delay: 0.5s;
}

.chart-card:nth-child(2) {
  animation-delay: 0.6s;
}

.table-card {
  animation-delay: 0.7s;
}

.summary-card {
  animation-delay: 0.8s;
}

/* Loading animations */
.loading-pulse {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

/* Hover effects */
.stats-card,
.chart-card,
.table-card,
.summary-card,
.filter-card {
  cursor: pointer;
}

.stats-card:hover .stats-icon,
.chart-card:hover .chart-title .v-icon,
.table-card:hover .table-title .v-icon,
.summary-card:hover .summary-title .v-icon {
  transform: scale(1.1);
  transition: transform 0.2s ease;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Print styles */
@media print {
  .dashboard-container {
    background: white !important;
    color: black !important;
    padding: 0 !important;
  }

  .stats-card,
  .chart-card,
  .table-card,
  .summary-card,
  .filter-card {
    background: white !important;
    color: black !important;
    box-shadow: none !important;
    border: 1px solid #ddd !important;
  }

  .dashboard-title,
  .dashboard-subtitle {
    color: black !important;
  }

  .filter-card {
    display: none !important;
  }

  .chart-container {
    height: 300px !important;
  }
}
</style>